import { Routes, Route } from 'react-router-dom'
import { getHostBinding } from '@/config/hostBinding'
import PlaybookInputPage from '@/pages/public/PlaybookInputPage'
import MarketplaceHome from '@/pages/marketplace/MarketplaceHome'
import MarketplaceAppPage from '@/pages/marketplace/MarketplaceAppPage'
import InstallPage from '@/pages/marketplace/InstallPage'
import MyAppsPage from '@/pages/marketplace/MyAppsPage'
import SellPage from '@/pages/marketplace/SellPage'
import SellerDashboard from '@/pages/marketplace/SellerDashboard'
import BuildGetItBuiltPage from '@/pages/marketplace/build/GetItBuiltPage'
import BuildPostRequestPage from '@/pages/marketplace/build/PostRequestPage'
import BuildRequestPage from '@/pages/marketplace/build/RequestPage'
import BuildQuotePage from '@/pages/marketplace/build/QuotePage'
import BuildPlaceOrderPage from '@/pages/marketplace/build/PlaceOrderPage'
import BuildOrderPage from '@/pages/marketplace/build/OrderPage'
import BuildFindWorkPage from '@/pages/marketplace/build/FindWorkPage'
import BuildSendEstimatePage from '@/pages/marketplace/build/SendEstimatePage'
import BuildBuilderProfilePage from '@/pages/marketplace/build/BuilderProfilePage'
import BuildBuilderDashboardPage from '@/pages/marketplace/build/BuilderDashboardPage'
import BuildHostingPricingPage from '@/pages/marketplace/build/HostingPricingPage'
import { Toaster } from 'react-hot-toast'
import { AuthProvider } from '@/auth/AuthContext'
import SqlBuilderProvider from '@/components/sqlbuilder/SqlBuilderProvider'
import { VisualEventNotesProvider } from '@twasta/visual-engine'
import { visualNotes } from '@/api/visualData'
import ProtectedRoute from '@/auth/ProtectedRoute'
import AppLayout from '@/components/layout/AppLayout'

// Auth pages
import LoginPage from '@/pages/auth/LoginPage'
import RegisterPage from '@/pages/auth/RegisterPage'
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage'
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage'
import ActivationPage from '@/pages/auth/ActivationPage'
import AcceptInvitationPage from '@/pages/auth/AcceptInvitationPage'
import WaitlistPage from '@/pages/auth/WaitlistPage'

// Workbench
import WorkbenchPage from '@/pages/workbench/WorkbenchPage'
import SettingsPage from '@/pages/workbench/SettingsPage'
import AgentApprovePage from '@/pages/AgentApprovePage'
import CreateFromGitPage from '@/pages/workbench/CreateFromGitPage'
import RunLocallyWizard from '@/pages/runtime/RunLocallyWizard'

// App generation
import DescribeAppPage from '@/pages/generateapp/DescribeAppPage'
import FinetuneFrsPage from '@/pages/generateapp/FinetuneFrsPage'
import PreviewAppPage from '@/pages/generateapp/PreviewAppPage'
import AutoAgentPage from '@/pages/autoagent/AutoAgentPage'

// Designers
import DatabaseDesignerPage from '@/pages/designers/DatabaseDesignerPage'
import WorkflowDesignerPage from '@/pages/designers/WorkflowDesignerPage'
import TransactionDesignerPage from '@/pages/designers/TransactionDesignerPage'
import PageDesignerPage from '@/pages/designers/PageDesignerPage'
import VisualDesignerPage from '@/pages/designers/VisualDesignerPage'
import ReportDesignerPage from '@/pages/designers/ReportDesignerPage'
import SchemaDesignerPage from '@/pages/designers/SchemaDesignerPage'
import BusinessAreaDesignerPage from '@/pages/designers/BusinessAreaDesignerPage'
import AssistantConfigPage from '@/pages/designers/AssistantConfigPage'
import ProcessFlowPage from '@/pages/designers/processflow/ProcessFlowPage'
import SchedulerDesignerPage from '@/pages/designers/SchedulerDesignerPage'
import PlaybookDesignerPage from '@/pages/designers/PlaybookDesignerPage'
import DashboardDesignerPage from '@/pages/designers/DashboardDesignerPage'
import GenProcessDesignerPage from '@/pages/designers/GenProcessDesignerPage'
import DocumentDesignerPage from '@/pages/designers/DocumentDesignerPage'
import DocumentFormDesignerPage from '@/pages/designers/DocumentFormDesignerPage'
import WizardDesignerPage from '@/pages/designers/WizardDesignerPage'
import DocumentFormListPage from '@/pages/designers/documentform/DocumentFormListPage'
import StylePlacementDesignerPage from '@/pages/designers/StylePlacementDesignerPage'

// Source Editor
import SourceEditorPage from '@/pages/srceditor/SourceEditorPage'

// Deploy
import DeployPage from '@/pages/deploy/DeployPage'

// Domains — hostnames that serve one application
import DomainsPage from '@/pages/domains/DomainsPage'
import TestingPage from '@/pages/testing/TestingPage'
import PaymentsPage from '@/pages/payments/PaymentsPage'
import SpamProtectionPage from '@/pages/captcha/SpamProtectionPage'
// Printers — device printing for wizard `print` steps (delivery: device)
import PrintersPage from '@/pages/printers/PrintersPage'
// HTTP connections — what a wizard `fetch_url` step's `request.connection` names
import HttpConnectionsPage from '@/pages/connections/HttpConnectionsPage'
import ActivityPage from '@/pages/activity/ActivityPage'
import WebsiteContentPage from '@/pages/content/WebsiteContentPage'
import DeploymentDetailPage from '@/pages/deploy/DeploymentDetailPage'
import J2eeDeployPage from '@/pages/workbench/J2eeDeployPage'

// Enterprise
import UsersPage from '@/pages/enterprise/UsersPage'
import SecurityPage from '@/pages/enterprise/SecurityPage'
import GatewaysPage from '@/pages/enterprise/GatewaysPage'

// Payments
import PlanSelectorPage from '@/pages/payments/PlanSelectorPage'
import BillingPage from '@/pages/billing/BillingPage'
import TenantBillingPage from '@/pages/enterprise/TenantBillingPage'
import PaymentGatewayPage from '@/pages/payments/PaymentGatewayPage'

// Documents (Notion-style document instances of document-mode pages)
import DocumentListPage from '@/pages/documents/DocumentListPage'

// Samples gallery (stub)
import SamplesGalleryPage from '@/pages/samples/SamplesGalleryPage'

// Instance-admin console (platform AI config + analytics)
import InstanceAdminPage from '@/pages/admin/InstanceAdminPage'

// Run — end-user view of an application, opened in a new tab
import RunPage, { RunRedirect } from '@/pages/run/RunPage'
// The return leg of "Sign in with Twasta" on an application's own domain
import SsoCallback from '@/pages/run/SsoCallback'

// Website viewer — public preview for external/internal smart pages
import WebsiteViewerPage from '@/pages/website/WebsiteViewerPage'

// Builder Mode — chat-first parallel interface for non-technical users
import { installCaptchaInterceptor } from '@/api/siteCaptcha'
import BuilderHubPage from '@/pages/builder/BuilderHubPage'
import BuilderWorkspacePage from '@/pages/builder/BuilderWorkspacePage'

// Test hooks for toasts (see packages/page-runtime/src/testHooks.ts): the UI
// acceptance executor reads a shown message by kind from these class names.
// react-hot-toast takes no data attributes, so a class is the hook here.
const TOAST_TEST_HOOKS = {
  className: 'tw-msg tw-msg-info',
  success: { className: 'tw-msg tw-msg-success' },
  error: { className: 'tw-msg tw-msg-error' },
  loading: { className: 'tw-msg tw-msg-loading' },
}

/**
 * The route tree for an application served at its OWN domain.
 *
 * Nothing about the platform is reachable here — no builder, no designers, no
 * other project — and that is not only a routing choice: the backend refuses
 * those endpoints on a bound host (`middleware/domain_scope`), so a route to
 * them would be a door onto a locked room. What the host serves is the one
 * application, at the root, with the URL free to describe the SCREEN rather
 * than to name the application over and over.
 *
 *   app  →  /                    the application
 *           /{object}            an open object, exactly as `/run/.../{object}`
 *   site →  /                    the published site's home page
 *           /login               its sign-in page
 *           /{page}              a page of it
 */
function HostBoundApp({ binding }: { binding: NonNullable<ReturnType<typeof getHostBinding>> }) {
  const identity = {
    tenant: binding.tenant_id || '_',
    project: binding.project,
    ownerUserId: binding.owner_user_id,
    platformUrl: binding.platform_url,
  }
  if (binding.target === 'site') {
    const siteIdentity = {
      userId: binding.owner_user_id,
      project: binding.project,
      siteSlug: binding.site_slug || 'main',
    }
    return (
      <AuthProvider>
        <Toaster position="top-right" toastOptions={TOAST_TEST_HOOKS} />
        <Routes>
          {/* An agent playbook's one-time link — before the page catch-alls. */}
          <Route path="/pb-input/:owner/:project/:token" element={<PlaybookInputPage />} />
          <Route path="/" element={<WebsiteViewerPage identity={siteIdentity} />} />
          <Route path="/login" element={<WebsiteViewerPage identity={siteIdentity} authPage />} />
          <Route path="/:pageSlug" element={<WebsiteViewerPage identity={siteIdentity} />} />
          <Route path="/*" element={<WebsiteViewerPage identity={siteIdentity} />} />
        </Routes>
      </AuthProvider>
    )
  }
  return (
    <AuthProvider>
      <Toaster position="top-right" toastOptions={TOAST_TEST_HOOKS} />
      <Routes>
        {/* Where "Sign in with Twasta" comes back to. The platform holds the
            session and the sign-in form; this host only ever receives a
            one-time code and exchanges it for a session of its own. The
            Twasta login page is deliberately NOT served here — an
            application's own domain is not where somebody should be asked
            for their platform password. */}
        <Route path="/sso/callback" element={<SsoCallback />} />
        {/* An agent playbook's one-time link, answered by someone outside. */}
        <Route path="/pb-input/:owner/:project/:token" element={<PlaybookInputPage />} />
        <Route path="/*" element={<RunPage identity={identity} />} />
      </Routes>
    </AuthProvider>
  )
}

// One request interceptor attaches the bot check to anonymous site WRITES —
// an embedded transaction save, a public file upload. Installed at module load
// so it is in place before any route renders, and idempotent.
installCaptchaInterceptor()

function App() {
  // Resolved before the first render (the server wrote it into the document),
  // so the shell never flashes the builder on somebody's own domain.
  const hostBinding = getHostBinding()
  if (hostBinding) return <HostBoundApp binding={hostBinding} />

  return (
    <AuthProvider>
      <Toaster position="top-right" toastOptions={TOAST_TEST_HOOKS} />
      {/* The SQL query builder behind every SQL input's "Query builder" button. */}
      <SqlBuilderProvider>
        {/* A chart's notes (visual events) reach /api/visual-data/notes. */}
        <VisualEventNotesProvider value={visualNotes}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LoginPage />} />
        {/* Alias: external SSO handoffs (runtime-authorize, IOFlow) link to /login */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/activate" element={<ActivationPage />} />
        {/* Public on purpose: it resolves the token first, then routes the
            invitee to register / sign in / accept as appropriate. */}
        <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
        <Route path="/waitlist" element={<WaitlistPage />} />
        {/* Public: an agent playbook's one-time link for someone with no
            account. The token in the address is the credential. */}
        <Route path="/pb-input/:owner/:project/:token" element={<PlaybookInputPage />} />

        {/* App Marketplace (docs/app_marketplace.md). Browsing is open to
            everyone; installing, My apps and selling need a Twasta sign-in.
            Its own chrome (MarketplaceShell), not the builder's AppLayout. */}
        <Route path="/marketplace" element={<MarketplaceHome />} />
        <Route path="/marketplace/app/:slug" element={<MarketplaceAppPage />} />
        <Route path="/marketplace/install/:slug" element={<ProtectedRoute><InstallPage /></ProtectedRoute>} />
        <Route path="/marketplace/my-apps" element={<ProtectedRoute><MyAppsPage /></ProtectedRoute>} />
        <Route path="/marketplace/sell" element={<ProtectedRoute><SellPage /></ProtectedRoute>} />
        <Route path="/marketplace/sell/:project" element={<ProtectedRoute><SellPage /></ProtectedRoute>} />
        <Route path="/marketplace/seller" element={<ProtectedRoute><SellerDashboard /></ProtectedRoute>} />
        {/* Get it built (docs/marketplace_get_it_built.md): custom apps from Twasta
            builders. The landing, hosting prices and builder profiles are public;
            everything that acts or shows an order needs a Twasta sign-in. */}
        <Route path="/marketplace/build" element={<BuildGetItBuiltPage />} />
        <Route path="/marketplace/build/new" element={<ProtectedRoute><BuildPostRequestPage /></ProtectedRoute>} />
        <Route path="/marketplace/build/new/:id" element={<ProtectedRoute><BuildPostRequestPage /></ProtectedRoute>} />
        <Route path="/marketplace/build/requests/:id" element={<ProtectedRoute><BuildRequestPage /></ProtectedRoute>} />
        <Route path="/marketplace/build/quotes/:id" element={<ProtectedRoute><BuildQuotePage /></ProtectedRoute>} />
        <Route path="/marketplace/build/order/:quoteId" element={<ProtectedRoute><BuildPlaceOrderPage /></ProtectedRoute>} />
        <Route path="/marketplace/build/orders/:id" element={<ProtectedRoute><BuildOrderPage /></ProtectedRoute>} />
        <Route path="/marketplace/work" element={<ProtectedRoute><BuildFindWorkPage /></ProtectedRoute>} />
        <Route path="/marketplace/work/:requestId" element={<ProtectedRoute><BuildSendEstimatePage /></ProtectedRoute>} />
        <Route path="/marketplace/builder" element={<ProtectedRoute><BuildBuilderDashboardPage /></ProtectedRoute>} />
        <Route path="/marketplace/builders/:tenantId" element={<BuildBuilderProfilePage />} />
        <Route path="/marketplace/hosting" element={<BuildHostingPricingPage />} />

        {/* Protected routes with layout */}
        <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
          {/* Optional :project/:tab segments make the workbench deep-linkable
              (back/forward + copy-paste URL re-open the exact place). Bare
              /workbench still works and is canonicalized by WorkbenchPage. */}
          <Route path="/workbench/:project?/:tab?/:sub?" element={<WorkbenchPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          {/* The link a `twasta` sign-in prints. Protected, so an
              anonymous visitor is sent to log in and returned here. */}
          <Route path="/agent-access" element={<AgentApprovePage />} />
          <Route path="/create-from-git" element={<CreateFromGitPage />} />
          <Route path="/runtime-package" element={<RunLocallyWizard />} />
          <Route path="/generateapp" element={<DescribeAppPage />} />
          <Route path="/generateapp/finetune" element={<FinetuneFrsPage />} />
          <Route path="/generateapp/preview" element={<PreviewAppPage />} />
          <Route path="/autoagent" element={<AutoAgentPage />} />
          <Route path="/designers/database/:project" element={<DatabaseDesignerPage />} />
          <Route path="/designers/workflow/:project/:objName" element={<WorkflowDesignerPage />} />
          <Route path="/designers/transaction/:project/:objName" element={<TransactionDesignerPage />} />
          <Route path="/designers/page/:project/:objName" element={<PageDesignerPage />} />
          <Route path="/designers/visual/:project/:objName" element={<VisualDesignerPage />} />
          <Route path="/designers/visual-schema/:project/:schemaName" element={<SchemaDesignerPage />} />
          <Route path="/designers/business-area/:project/:areaName" element={<BusinessAreaDesignerPage />} />
          <Route path="/designers/assistant/:project" element={<AssistantConfigPage />} />
          <Route path="/designers/process-flow/:project" element={<ProcessFlowPage />} />
          <Route path="/designers/scheduler/:project" element={<SchedulerDesignerPage />} />
          <Route path="/designers/playbooks/:project" element={<PlaybookDesignerPage />} />
          <Route path="/designers/dashboard/:project/:objName" element={<DashboardDesignerPage />} />
          <Route path="/designers/genprocess/:project/:objName" element={<GenProcessDesignerPage />} />
          <Route path="/designers/report/:project/:objName" element={<ReportDesignerPage />} />
          <Route path="/designers/document/:project/:objName" element={<DocumentDesignerPage />} />
          {/* The LIST comes first: a document form has no navigation
              entry (it is a view of its transaction), so this is the
              only door to its designer — reached from Advanced. */}
          <Route path="/designers/document-form/:project" element={<DocumentFormListPage />} />
          <Route path="/designers/document-form/:project/:objName" element={<DocumentFormDesignerPage />} />
          <Route path="/designers/wizard/:project/:objName" element={<WizardDesignerPage />} />
          <Route path="/designers/style-placement/:masterId" element={<StylePlacementDesignerPage />} />
          <Route path="/srceditor/:project" element={<SourceEditorPage />} />
          <Route path="/deploy/:project" element={<DeployPage />} />
          <Route path="/deploy/:project/history/:deploymentId" element={<DeploymentDetailPage />} />
          <Route path="/j2ee-deploy/:project" element={<J2eeDeployPage />} />
          <Route path="/domains/:project" element={<DomainsPage />} />
          <Route path="/testing/:project" element={<TestingPage />} />
          <Route path="/payments/:project" element={<PaymentsPage />} />
          <Route path="/spam-protection/:project" element={<SpamProtectionPage />} />
          <Route path="/printers/:project" element={<PrintersPage />} />
          <Route path="/http-connections/:project" element={<HttpConnectionsPage />} />
          <Route path="/activity/:project" element={<ActivityPage />} />
          <Route path="/website-content/:project" element={<WebsiteContentPage />} />
          <Route path="/enterprise/users" element={<UsersPage />} />
          <Route path="/enterprise/security" element={<SecurityPage />} />
          <Route path="/enterprise/gateways" element={<GatewaysPage />} />
          <Route path="/enterprise/activity" element={<ActivityPage scope="org" />} />
          <Route path="/admin" element={<InstanceAdminPage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/enterprise/billing" element={<TenantBillingPage />} />
          <Route path="/payments/plans" element={<PlanSelectorPage />} />
          <Route path="/payments/gateway" element={<PaymentGatewayPage />} />
          <Route path="/documents/:project" element={<DocumentListPage />} />
          <Route path="/samples" element={<SamplesGalleryPage />} />
          <Route path="/builder" element={<BuilderHubPage />} />
          <Route path="/builder/:project" element={<BuilderWorkspacePage />} />
        </Route>

        {/* Public website viewer — external pages require no auth;
            internal pages redirect to login when JWT is absent. */}
        <Route path="/site/:userId/:project/:siteSlug" element={<WebsiteViewerPage />} />
        {/* Dedicated sign-in page. Static `login` segment outranks the
            dynamic `:pageSlug` route, so it always matches here. A `?next=`
            query carries the gated page the visitor was trying to reach so we
            can forward them there after a successful sign-in. */}
        <Route path="/site/:userId/:project/:siteSlug/login" element={<WebsiteViewerPage authPage />} />
        <Route path="/site/:userId/:project/:siteSlug/:pageSlug" element={<WebsiteViewerPage />} />
        {/* Deeper paths — a blog post (`blog/<slug>`), list page 2, a tag or
            author page — are resolved by the server from the collection. */}
        <Route path="/site/:userId/:project/:siteSlug/*" element={<WebsiteViewerPage />} />

        {/* End-user "Run" view — no AppLayout chrome (sidebar / topbar)
            because this is meant to look like the deployed app to an end
            user. Opened in a new browser tab from the Run button in TopBar.
            NOT wrapped in ProtectedRoute: RunPage decides who is running it
            (a Twasta account or the application's own PROJECT USER) and,
            with no session at all, offers the application's own sign-in.
            The platform gate redirected a project user — who has no Twasta
            account — to the platform login before RunPage could decide. */}
        <Route path="/run/:tenant/:project/*" element={<RunPage />} />
        {/* Legacy / tenant-less link → redirect to the tenant-scoped URL. */}
        <Route
          path="/run/:project"
          element={
            <ProtectedRoute>
              <RunRedirect />
            </ProtectedRoute>
          }
        />
      </Routes>
        </VisualEventNotesProvider>
      </SqlBuilderProvider>
    </AuthProvider>
  )
}

export default App
